// LT-295: CRM conflict-resolution card. Lists mappings with sync_status
// = `conflict` and their field-level Cold Caller vs CRM diff. Admin
// gets two resolve buttons per row ("Use Cold Caller value" /
// "Use CRM value"); members see the same list read-only (backend
// returns 403 on mutation; the component hides the buttons when
// canResolve=false).
import React, { useEffect, useState } from 'react';
import {
  CRMConflictDetail,
  asCrmApiError,
  crmSyncAPI,
} from '../services/crmSyncService';
import { APP_NAME } from '../config/region';

export interface CrmConflictsCardProps {
  canResolve?: boolean;
  onResolved?: () => void;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '(unserialisable)';
  }
}

const CrmConflictsCard: React.FC<CrmConflictsCardProps> = ({
  canResolve = true,
  onResolved,
}) => {
  const [conflicts, setConflicts] = useState<CRMConflictDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await crmSyncAPI.listConflicts();
      setConflicts(rows);
    } catch (e) {
      setError(asCrmApiError(e).message || 'Failed to load conflicts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const resolve = async (c: CRMConflictDetail, side: 'local' | 'remote') => {
    setResolvingId(c.id);
    setError(null);
    try {
      await crmSyncAPI.resolveConflict(c.id, side);
      // Drop the row optimistically.
      setConflicts((prev) => prev.filter((r) => r.id !== c.id));
      onResolved?.();
    } catch (e) {
      setError(asCrmApiError(e).message || 'Resolve failed.');
    } finally {
      setResolvingId(null);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="crm-conflicts-card"
        className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
      >
        <p className="text-gray-400">Loading conflicts…</p>
      </section>
    );
  }

  if (conflicts.length === 0 && !error) {
    // Empty state — render nothing so the Settings page is tidy when
    // everything is in sync.
    return null;
  }

  return (
    <section
      data-testid="crm-conflicts-card"
      className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
    >
      <h2 className="text-white text-base font-medium mb-2">
        Conflicts ({conflicts.length})
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        These records were edited in both {APP_NAME} and the CRM after
        the last successful pull. Pick the winning side to unblock sync.
      </p>
      {error && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      <ul className="space-y-4" data-testid="crm-conflicts-list">
        {conflicts.map((c) => (
          <li
            key={c.id}
            className="rounded border border-white/10 bg-slate-900/40 p-3"
          >
            <div className="flex items-center justify-between mb-2 gap-4">
              <div className="text-xs text-gray-400 uppercase tracking-wide">
                {c.local_entity_type} · remote {c.remote_entity_id}
              </div>
              {canResolve && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={resolvingId === c.id}
                    onClick={() => resolve(c, 'local')}
                    className="px-3 py-1.5 text-xs border border-emerald-500/40 text-emerald-200 rounded hover:bg-emerald-500/10 disabled:opacity-50"
                  >
                    Use {APP_NAME} value
                  </button>
                  <button
                    type="button"
                    disabled={resolvingId === c.id}
                    onClick={() => resolve(c, 'remote')}
                    className="px-3 py-1.5 text-xs border border-blue-500/40 text-blue-200 rounded hover:bg-blue-500/10 disabled:opacity-50"
                  >
                    Use CRM value
                  </button>
                </div>
              )}
            </div>
            {c.conflicting_fields.length === 0 ? (
              <p className="text-xs text-gray-400">
                No field-level diff available; resolver will clear the
                conflict flag on the mapping.
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 uppercase tracking-wide">
                    <th className="py-1 pr-4">Field</th>
                    <th className="py-1 pr-4">{APP_NAME}</th>
                    <th className="py-1">CRM</th>
                  </tr>
                </thead>
                <tbody>
                  {c.conflicting_fields.map((field) => (
                    <tr
                      key={field}
                      className="border-t border-white/5 text-gray-200"
                    >
                      <td className="py-1 pr-4 font-medium">{field}</td>
                      <td className="py-1 pr-4">
                        {formatValue(
                          (c.local_data as Record<string, unknown> | null)?.[field],
                        )}
                      </td>
                      <td className="py-1">
                        {formatValue(
                          (c.remote_data as Record<string, unknown> | null)?.[field],
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default CrmConflictsCard;
